// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React from 'react';
import { arrayOf, bool, number, object, string } from 'prop-types';
import { isFunction, get } from 'lodash';
import cx from 'classnames';
import {
  Checkbox,
  Link,
  TableRow,
  TableCell,
  Tooltip,
} from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import StatusContent from '../StatusContent';
import { func } from 'prop-types';
const getHyperlinkCellContent = ({ uuid, linkValue, format, value }) => (
    <Link underline="none" href={`${linkValue}/${uuid}`}>
      {format ? format(value) : value}
    </Link>
);

const getPlainCellContent = ({ format, value, uuid, secondaryValue,
  isStatusIcon, notifyMessages, message, id, classes, isCellOverflow }) => {
   const content = format ? format(value, secondaryValue, uuid) : value;
  return(
    isStatusIcon ?
      <StatusContent
        id={id}
        statusLabel={content}
        notifyMessages={notifyMessages}
        message={message}
      />
    :
    <span className={isCellOverflow ? classes.cellOverflow: ''}>
      {content}
    </span>
  );
};

const PlainTableRow = ({ userContext, columns = [], uuidName,
  notifyMessages, hasBulkActions, selectedStyles, numSelectedRows,
  classes, row, dragAndDropRef, opacity, allowOrdering }) => {
  return (
  <TableRow ref={dragAndDropRef} style={{ opacity }}
      className={cx({ [selectedStyles]: row.selected }, get(row, 'classes.root'))}
      hover tabIndex={-1} key={row.uuid}
      onClick={() => {
        if (isFunction(row.onClick)) { row.onClick(row.uuid); }
      }}
      selected={row.selected}
    >
      {hasBulkActions &&
        <TableCell padding="checkbox">
          <Tooltip arrow title={row.checkBoxTooltip || ''}>
            <span>
              <Checkbox
                checked={row.selected}
                onClick={e => e.stopPropagation()}
                onChange={e => row.onSelectToggle(e, row)}
                inputProps={{ 'aria-labelledby': row.uuid }}
                disabled={row.disabled}
              />
            </span>
          </Tooltip>
        </TableCell>
      }
      {allowOrdering &&
        <TableCell>
          <DragIndicatorIcon
            className={row.draggable ? classes.draggableIcon : classes.disabledDrag} />
        </TableCell>
      }
      {columns.map(column => {
        const {
          id,
          link,
          value,
          isStatusIcon,
          isCellOverflow,
          secondaryId,
          isHidden=false,
        } = column;
        const linkValue = link && isFunction(link) ? link({ ...row, userContext }) : link;
        const content = linkValue
          ? getHyperlinkCellContent({
            ...column,
            uuid: uuidName ? row[uuidName] :row.uuid,
            uuidName,
            value: value && isFunction(value) ? value(row) : row[id],
            linkValue,
          })
          : getPlainCellContent({
            ...column,
            value: value && isFunction(value) ? value({ ...row, numSelectedRows }) : row[id],
            secondaryValue: secondaryId ? row[secondaryId] : null,
            uuid: uuidName ? row[uuidName] :row.uuid,
            isStatusIcon,
            notifyMessages,
            message: get(row, 'message'),
            id,
            classes,
            isCellOverflow,
          });
          if(!isHidden) {
            return (
              <TableCell key={id} align="left" style={{ verticalAlign:'middle' }}>
                {content}
              </TableCell>
            );
          }
      })}
    </TableRow>)
};

export default PlainTableRow;

getHyperlinkCellContent.propTypes = {
  uuid: string,
  linkValue: string,
  format: string,
  value: string || number,
};

getPlainCellContent.propTypes = {
  format: string, value: string || number,
};

PlainTableRow.propTypes = {
  userContext: object,
  columns: arrayOf(object),
  id: string,
  noResultsMessage: string,
  hasBulkActions: bool,
  selectedRows: arrayOf(string),
  onSelectAllRowsToggle: func,
  row: object,
  uuidName: string,
  notifyMessages: func,
  selectedStyles: string,
  numSelectedRows: number,
  classes: object,
  dragAndDropRef: object,
  opacity: number,
  allowOrdering: bool,
};